import { useEffect, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  Shield, FileText, AlertTriangle, CheckCircle2, Activity, ClipboardCheck,
  TrendingUp, Sparkles, ArrowRight, ShieldAlert, ClipboardList, Scale, FileSignature,
  HardDrive, Users2, Trophy, ChevronDown as ChevronDownIcon,
} from 'lucide-react';
import Header from '../components/Header';
import DIASCommandCentre from '../components/DIASCommandCentre';
import EquipmentPage from './EquipmentPage';
import { LicensingTable } from './PeoplePage';
import { SECTIONS } from '../lib/sectionTheme';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const C = SECTIONS.compliance;
const E = SECTIONS.equipment;

export default function CompliancePage() {
  const { user } = useAuth();
  const isPM = user?.role === 'admin' || user?.role === 'superuser';

  const [searchParams, setSearchParams] = useSearchParams();
  // Tab state synced to ?tab= URL param so deep-links and the
  // /equipment redirect both land on the right tab.
  const tab = searchParams.get('tab') || 'overview';
  const setTab = (next) => {
    const sp = new URLSearchParams(searchParams);
    if (next === 'overview') sp.delete('tab'); else sp.set('tab', next);
    setSearchParams(sp, { replace: true });
  };

  const [governance, setGovernance] = useState(null);
  const [sopStats, setSopStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [staffUsers, setStaffUsers] = useState([]);
  const [staffExpiring, setStaffExpiring] = useState([]);
  const [staffSummary, setStaffSummary] = useState(null);
  const [verifBoard, setVerifBoard] = useState(null);
  const [diasModules, setDiasModules] = useState([]);  // /api/dias-validator → modules[]

  useEffect(() => {
    if (!isPM) return;  // non-PM users will be redirected before any fetch
    const reqs = [
      axios.get(`${API}/sops/stats`, { withCredentials: true }).then(r => setSopStats(r.data)).catch(() => {}),
      axios.get(`${API}/sops-governance/overview`, { withCredentials: true }).then(r => setGovernance(r.data)).catch(() => {}),
      // Staff licensing roster — same endpoint that powers People → Licensing.
      axios.get(`${API}/users`, { withCredentials: true }).then(r => setStaffUsers(r.data || [])).catch(() => {}),
      axios.get(`${API}/users/licensing/expiring`, { withCredentials: true }).then(r => {
        const d = r.data || {};
        setStaffExpiring(d.expiring || []);
        setStaffSummary(d.summary || null);
      }).catch(() => {}),
      axios.get(`${API}/verifications/leaderboard`, { withCredentials: true })
        .then(r => setVerifBoard(r.data)).catch(() => {}),
      // Live DIAS validator status feeds the per-module pass/warn/fail pills.
      axios.get(`${API}/dias-validator`, { withCredentials: true })
        .then(r => setDiasModules(r.data?.modules || [])).catch(() => {}),
    ];
    Promise.all(reqs).finally(() => setLoading(false));
  }, [isPM]);

  // DIAS hub is admin/superuser only — non-PM users typing the URL directly
  // get bounced back to the dashboard.
  if (user && !isPM) return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen bg-background" data-testid="compliance-page">
      <Header />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Condensed hero — DIAS-first framing */}
        <div className="mb-5 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-2 px-2.5 py-0.5 rounded-full mb-1.5" style={{ backgroundColor: `${C.color}1A`, color: C.color }}>
              <Sparkles className="w-3 h-3" />
              <span className="text-[9px] font-bold uppercase tracking-wider">DIAS — Diagnostic Imaging Accreditation Scheme</span>
            </div>
            <h1 className="font-['Outfit'] text-2xl sm:text-3xl font-bold tracking-tight text-foreground">
              Compliance hub
            </h1>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {isPM && (
              <Link to="/sops/governance" data-testid="compliance-governance-action"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white rounded-lg"
                style={{ backgroundColor: C.color }}>
                <Shield className="w-3.5 h-3.5" /> Governance dashboard
              </Link>
            )}
          </div>
        </div>

        {/* Tab pills — Overview · Equipment · Staff licensing.
            Synced to ?tab= URL param so deep-links + the /equipment redirect
            land on the right view. Overview is the default. */}
        <div className="mb-6 flex items-center gap-2 flex-wrap" data-testid="compliance-tabs">
          <TabPill label="Overview"        icon={Sparkles}    color={C.color} active={tab === 'overview'}  onClick={() => setTab('overview')}  testid="compliance-tab-overview" />
          <TabPill label="Equipment"       icon={HardDrive}   color={E.color} active={tab === 'equipment'} onClick={() => setTab('equipment')} testid="compliance-tab-equipment" />
          <TabPill label="Staff licensing" icon={Users2}      color="#0891B2" active={tab === 'staff'}     onClick={() => setTab('staff')}     testid="compliance-tab-staff" />
        </div>

        {tab === 'equipment' && <EquipmentPage embedded />}

        {tab === 'staff' && (
          <div data-testid="compliance-staff-panel">
            {staffSummary && (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4" data-testid="compliance-staff-kpis">
                <KPI icon={AlertTriangle} label="Expired"
                  value={staffSummary.expired ?? 0}
                  color={(staffSummary.expired ?? 0) > 0 ? '#DC2626' : '#64748B'} />
                <KPI icon={TrendingUp} label="Expiring ≤30 days"
                  value={staffSummary.expiring_soon ?? 0}
                  color={(staffSummary.expiring_soon ?? 0) > 0 ? '#D97706' : '#64748B'} />
                <KPI icon={CheckCircle2} label="Current" value={staffSummary.current ?? 0} color="#16A34A" />
                <KPI icon={Users2} label="Missing licence" value={staffSummary.missing ?? 0}
                  color={(staffSummary.missing ?? 0) > 0 ? '#0EA5E9' : '#64748B'} />
              </div>
            )}
            <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
              <div>
                <h2 className="font-['Outfit'] text-xl font-semibold text-foreground">Staff licensing roster</h2>
                <p className="text-xs text-muted-foreground mt-1 max-w-xl">
                  AHPRA / radiation licence / BLS / contrast accreditation expiry across every clinical staff member. To edit a row, jump into the People page.
                </p>
              </div>
              <Link to="/people?tab=licensing"
                data-testid="compliance-staff-open-people"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white rounded-lg"
                style={{ backgroundColor: '#0891B2' }}>
                <Users2 className="w-3.5 h-3.5" /> Edit in People <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
            <VerifLeaderboard board={verifBoard} />
            <LicensingTable users={staffUsers} expiring={staffExpiring} />
          </div>
        )}

        {/* Overview tab — everything below. Hidden on the Equipment / Staff
            tabs so the page doesn't show two views at once. */}
        {tab === 'overview' && (
        <>
        {/* Top KPI row — always visible */}
        {sopStats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="compliance-stats">
            <KPI icon={TrendingUp} label="SOP compliance"
              value={`${sopStats.compliance_rate}%`}
              color={sopStats.compliance_rate >= 80 ? '#16A34A' : sopStats.compliance_rate >= 50 ? '#F59E0B' : '#EF4444'} />
            <KPI icon={ClipboardCheck} label="Active SOPs" value={sopStats.total_sops} color={SECTIONS.sops.color} />
            <KPI icon={CheckCircle2} label="Completed tasks" value={sopStats.completed} color="#16A34A" />
            <KPI icon={AlertTriangle} label="Overdue SOP tasks" value={sopStats.overdue}
              color={sopStats.overdue > 0 ? '#EF4444' : '#64748B'} />
          </div>
        )}

        {/* DIAS Command Centre (admin / PM only) — consolidates Scorecard + Master Checklist + Evidence audit log */}
        {isPM && <DIASCommandCentre />}

        {/* Governance review row (PM only) — compact banner.
            Full dashboard linked from the hero CTA. */}
        {isPM && governance && (
          <div className="bg-card border border-border rounded-xl px-4 py-2 mb-4 flex items-center gap-3 flex-wrap text-xs" data-testid="compliance-governance">
            <Shield className="w-3.5 h-3.5" style={{ color: C.color }} />
            <span className="font-semibold text-foreground">SOP review cycle</span>
            <span className="text-red-600 dark:text-red-400 font-mono">⚠ {governance.summary?.overdue || 0} overdue</span>
            <span className="text-amber-600 dark:text-amber-400 font-mono">{governance.summary?.due_soon || 0} due soon</span>
            <span className="text-emerald-600 dark:text-emerald-400 font-mono">✓ {governance.summary?.current || 0} current</span>
            <Link to="/sops/governance" className="ml-auto text-xs font-semibold inline-flex items-center gap-1" style={{ color: C.color }} data-testid="compliance-governance-link">
              Open <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
        )}

        {/* DIAS-aligned module groups — 5 modules matching the validator
            (Quality Management · Patient Care · Information Management ·
             Workforce · Safety, Quality & Risk). Each group is collapsible
            and shows a live status pill from /api/dias-validator. By default
            only modules with warnings/fails stay expanded so PMs see what
            needs attention. */}
        <div className="mb-8" data-testid="compliance-dias-modules">
          <h2 className="font-['Outfit'] text-lg font-semibold text-foreground mb-1">DIAS modules</h2>
          <p className="text-xs text-muted-foreground mb-3">Live status from the DIAS validator. Modules with anything failing or warning are expanded by default.</p>
          <div className="space-y-2">
            <DiasModuleGroup
              modulePrefix="1. Quality Management"
              icon={ClipboardCheck} color={SECTIONS.sops.color}
              diasModules={diasModules}
              cards={[
                { to: '/sops', icon: ClipboardCheck, title: 'Standard Operating Procedures',
                  desc: 'Seeded + custom SOPs, per-staff assignments, step-by-step walkthrough', color: SECTIONS.sops.color, testid: 'compliance-sops-card' },
                { to: '/sops/governance', icon: Shield, title: 'SOP governance',
                  desc: '12-month review cycle, sign-off register, ISO alignment', color: '#7C3AED', testid: 'compliance-governance-card' },
              ]} />

            <DiasModuleGroup
              modulePrefix="2. Patient Care"
              icon={FileText} color={SECTIONS.preparation.color}
              diasModules={diasModules}
              cards={[
                { to: '/preparation', icon: FileText, title: 'Patient preparation library',
                  desc: 'Prep notes for every exam and special patient groups', color: SECTIONS.preparation.color, testid: 'compliance-prep-card' },
                { to: '/screening', icon: FileText, title: 'Patient screening forms',
                  desc: 'RANZCR contrast, blood-thinner, pregnancy, consent', color: '#0891B2', testid: 'compliance-screening-card' },
                { to: '/consent', icon: FileSignature, title: 'Image-guided procedure consent',
                  desc: 'Montgomery-standard consent for spinal injections, biopsy, PRP, FNA', color: '#7C2D12', testid: 'compliance-consent-card' },
              ]} />

            <DiasModuleGroup
              modulePrefix="3. Information Management"
              icon={ShieldAlert} color="#42CDD7"
              diasModules={diasModules}
              cards={[
                { to: '/practice-settings', icon: ClipboardCheck, title: 'Practice settings',
                  desc: 'LSPN, HPI-O, ABN, accreditation #s, address, default reporting radiologist', color: '#42CDD7', adminOnly: true, isPM, testid: 'compliance-practice-card' },
                { to: '/mbs', icon: Activity, title: 'MBS fee compliance',
                  desc: 'Quarterly MBS fee-schedule change tracker', color: SECTIONS.mbs.color, testid: 'compliance-mbs-card' },
                { to: '/dashboard/logins', icon: ShieldAlert, title: 'Login activity & audit',
                  desc: 'Every auth event with IP, device, alerts for suspicious activity', color: '#EF4444', adminOnly: true, isPM, testid: 'compliance-logins-card' },
              ]} />

            <DiasModuleGroup
              modulePrefix="4. Workforce"
              icon={Users2} color="#0891B2"
              diasModules={diasModules}
              cards={[
                { onClick: () => setTab('staff'), icon: Users2, title: 'Staff licensing roster',
                  desc: 'AHPRA / ASAR / radiation / BLS expiry across every clinical staff member',
                  color: '#0891B2', testid: 'compliance-staff-card' },
                { to: '/disputes', icon: Scale, title: 'Conflict & dispute register',
                  desc: 'Staff complaints, mediation outcomes, audit trail', color: '#7C3AED', adminOnly: true, isPM, testid: 'compliance-disputes-card' },
              ]} />

            <DiasModuleGroup
              modulePrefix="5. Safety, Quality & Risk"
              icon={ShieldAlert} color="#DC2626"
              diasModules={diasModules}
              cards={[
                { to: '/ipc', icon: ClipboardList, title: 'Infection Prevention & Control',
                  desc: 'Daily cleaning log, antiseptic bottle register, monthly audit', color: '#DC2626', testid: 'compliance-ipc-card' },
                { to: '/incidents', icon: AlertTriangle, title: 'Incident reporting',
                  desc: 'Near misses, patient safety events, complaints, equipment + WHS', color: '#DC2626', testid: 'compliance-incidents-card' },
                { to: '/radiation-governance', icon: Shield, title: 'Radiation governance',
                  desc: 'RSO appointment, EPA licence, TLD readings, lead apron register', color: '#7C3AED', testid: 'compliance-radgov-card' },
                { onClick: () => setTab('equipment'), icon: HardDrive, title: 'Equipment register',
                  desc: 'DIAS asset list — serials, components, service history, RSO licences',
                  color: SECTIONS.equipment.color, testid: 'compliance-equipment-card' },
              ]} />
          </div>
        </div>

        {/* Framework alignment — aspirational, read-only */}
        <div className="bg-card border border-border rounded-2xl p-6" data-testid="compliance-frameworks">
          <h2 className="font-['Outfit'] text-lg font-semibold text-foreground flex items-center gap-2 mb-3">
            <Shield className="w-4 h-4" style={{ color: C.color }} />
            Framework alignment
          </h2>
          <p className="text-xs text-muted-foreground mb-4">
            The app is built around the following accreditation and regulatory frameworks. Upload evidence PDFs directly to each module's audit trail.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Framework title="DIAS — Diagnostic Imaging Accreditation Scheme"
              items={['Std 1 Governance', 'Std 2 Patient ID & Safety', 'Std 4 Medication Safety', 'Std 5 Infection Control', 'Std 6 Incident Management']} />
            <Framework title="ISO 15189 — Medical Laboratories"
              items={['§4.14 Incident management', 'Quality manual alignment', 'Internal audit cycle']} />
            <Framework title="RANZCR"
              items={['Iodinated Contrast Media Guideline V2.3', 'Gadolinium Guidance', 'Urgent & Critical Findings communication']} />
            <Framework title="Work Health & Safety"
              items={['WHS Act 2011', 'Zero-tolerance policy', 'Staff de-escalation training']} />
          </div>
        </div>

        {loading && <p className="mt-6 text-center text-xs text-muted-foreground">Loading live data…</p>}
        </>
        )}
      </div>
    </div>
  );
}

function VerifLeaderboard({ board }) {
  if (!board || !board.top || board.top.length === 0) return null;
  const total = board.totals?.total ?? 0;
  return (
    <div className="mb-4 border border-border rounded-xl p-3 bg-amber-500/5" data-testid="compliance-verif-leaderboard">
      <div className="flex items-center gap-2 mb-2">
        <Trophy className="w-4 h-4 text-amber-600" />
        <span className="text-xs font-semibold text-foreground">Verified by team — last {board.days} days</span>
        <span className="ml-auto text-[10px] font-mono text-muted-foreground">{total} total</span>
      </div>
      <table className="w-full text-[11px]">
        <thead className="text-left text-muted-foreground">
          <tr>
            <th className="pb-1 w-6">#</th>
            <th>Team member</th>
            <th className="text-right">AHPRA</th>
            <th className="text-right">HealthLink</th>
            <th className="text-right pr-1">Total</th>
          </tr>
        </thead>
        <tbody>
          {board.top.map((row, i) => (
            <tr key={row.by} className="border-t border-border" data-testid={`compliance-verif-row-${i}`}>
              <td className="py-1 font-mono text-muted-foreground">{i + 1}</td>
              <td className="font-medium text-foreground truncate max-w-xs">{row.by}</td>
              <td className="text-right tabular-nums">{row.ahpra}</td>
              <td className="text-right tabular-nums">{row.healthlink}</td>
              <td className="text-right pr-1 tabular-nums font-bold text-amber-700 dark:text-amber-300">{row.total}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TabPill({ label, icon: Icon, color, active, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-semibold rounded-full border transition-colors"
      style={active
        ? { backgroundColor: `${color}15`, borderColor: `${color}50`, color }
        : { borderColor: 'hsl(var(--border))', color: 'hsl(var(--muted-foreground))' }}>
      <Icon className="w-3.5 h-3.5" />
      {label}
    </button>
  );
}

function KPI({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-card border border-border rounded-2xl p-5">
      <div className="flex items-center gap-2 mb-2">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
          <Icon className="w-4 h-4" style={{ color }} />
        </div>
        <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</span>
      </div>
      <p className="font-['Outfit'] text-3xl font-bold text-foreground">{value}</p>
    </div>
  );
}

function DiasModuleGroup({ modulePrefix, icon: Icon, color, diasModules, cards }) {
  // Match the validator's module by prefix (e.g. "1. Quality Management")
  const mod = diasModules.find(m => (m.label || '').startsWith(modulePrefix.split(' ')[0]));
  const checks = mod?.checks || [];
  const counts = {
    pass:    checks.filter(c => c.status === 'pass').length,
    warning: checks.filter(c => c.status === 'warning').length,
    fail:    checks.filter(c => c.status === 'fail').length,
    manual:  checks.filter(c => c.status === 'manual_only').length,
  };
  const hasIssues = counts.fail > 0 || counts.warning > 0;
  // Default expanded only when there's something needing attention.
  const [open, setOpen] = useState(hasIssues);
  // Re-open when validator data arrives later (initial render had no checks).
  useEffect(() => { if (hasIssues) setOpen(true); }, [hasIssues]);

  const dotColor = counts.fail > 0 ? '#DC2626'
                  : counts.warning > 0 ? '#D97706'
                  : '#16A34A';
  const tid = `compliance-dias-group-${modulePrefix.split('.')[0]}`;

  return (
    <div className="border border-border rounded-xl bg-card overflow-hidden" data-testid={tid}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-muted/30 transition-colors"
        data-testid={`${tid}-toggle`}
      >
        <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${color}15` }}>
          <Icon className="w-4 h-4" style={{ color }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-['Outfit'] text-sm font-semibold text-foreground truncate">{modulePrefix}</h3>
            {checks.length > 0 && (
              <span className="inline-flex items-center gap-1 text-[10px] font-mono">
                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: dotColor }} />
                <span className="text-emerald-600 dark:text-emerald-400">{counts.pass}✓</span>
                {counts.warning > 0 && <span className="text-amber-600 dark:text-amber-400">{counts.warning}⚠</span>}
                {counts.fail > 0 && <span className="text-red-600 dark:text-red-400">{counts.fail}✗</span>}
                {counts.manual > 0 && <span className="text-sky-600 dark:text-sky-400">{counts.manual}●</span>}
              </span>
            )}
          </div>
          <p className="text-[11px] text-muted-foreground">{cards.length} card{cards.length === 1 ? '' : 's'}</p>
        </div>
        <ChevronDownIcon className={`w-4 h-4 text-muted-foreground transition-transform shrink-0 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-4 pb-4 pt-1 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid={`${tid}-cards`}>
          {cards.map(c => <ModuleCard key={c.testid} {...c} />)}
        </div>
      )}
    </div>
  );
}

function ModuleCard({ to, onClick, icon: Icon, title, desc, color, adminOnly, isPM, testid }) {
  if (adminOnly && !isPM) return null;
  const inner = (
    <>
      <div className="flex items-start justify-between mb-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
          <Icon className="w-4 h-4" style={{ color }} />
        </div>
        <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:translate-x-1 transition-transform" />
      </div>
      <h3 className="font-['Outfit'] text-sm font-semibold text-foreground">{title}</h3>
      <p className="text-xs text-muted-foreground mt-1">{desc}</p>
    </>
  );
  const cls = "group relative overflow-hidden bg-card border border-border rounded-2xl p-5 hover:shadow-sm transition-all text-left w-full";
  if (onClick) {
    return (
      <button type="button" onClick={onClick} data-testid={testid} className={cls}
        style={{ borderLeftWidth: 3, borderLeftColor: color }}>
        {inner}
      </button>
    );
  }
  return (
    <Link to={to} data-testid={testid} className={cls}
      style={{ borderLeftWidth: 3, borderLeftColor: color }}>
      {inner}
    </Link>
  );
}

function Framework({ title, items }) {
  return (
    <div className="p-4 rounded-xl bg-muted/30 border border-border/60">
      <p className="text-sm font-semibold text-foreground mb-2">{title}</p>
      <ul className="space-y-1 text-xs text-muted-foreground">
        {items.map(i => <li key={i} className="flex items-start gap-1.5"><CheckCircle2 className="w-3 h-3 text-green-500 mt-0.5 shrink-0" /> {i}</li>)}
      </ul>
    </div>
  );
}
